/**
 * useGlobalReservationSync.ts
 *
 * アプリケーション全体での予約データ同期管理用フック
 * App.jsなどで使用し、コンポーネントのマウント状況に関わらず同期を継続させる
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { reservationSyncService } from '../services/reservationSyncService';
import { vehicleSyncService } from '../services/vehicleSyncService';
import { pusherService } from '../services/pusherService';
import { useAuth } from '../components/AuthContext';
import { isPusherEnabled } from '../config/backend';
import { SAFETY_POLL_INTERVAL_MS } from '../constants/calendar';
import { syncStaffPrioritiesFromServer } from '../api/dispatchService';

// 復帰トリガー再同期のスロットル間隔（visibilitychange / online / Pusher再接続が
// 連続発火しても、この間隔内は1回だけ実行する）
const RESYNC_THROTTLE_MS = 5000;

const POLLING_COOKIE = 'calv2_polling_enabled';

function getPollingCookie(): boolean {
    try {
        const raw = document.cookie.split('; ').find(c => c.startsWith(`${POLLING_COOKIE}=`));
        return raw ? raw.split('=')[1] === 'true' : false;
    } catch { return false; }
}

function setPollingCookie(enabled: boolean): void {
    document.cookie = `${POLLING_COOKIE}=${enabled}; path=/; max-age=${60 * 60 * 24 * 365}`;
}

export function useGlobalReservationSync(enabled: boolean = true) {
    // Pusher を使わないインスタンス（宮古）はポーリングを強制ON。
    // 宮古は那覇との混線防止で Pusher を無効化しており（isPusherEnabled 参照）、
    // ポーリング（SYNC_INTERVAL_MS=30秒）がリアルタイム同期の代替となる。
    // cookie のトグル状態より優先し、OFF にはできない。
    const pollingForced = !isPusherEnabled();
    const [pollingCookieOn, setPollingCookieOn] = useState<boolean>(getPollingCookie);
    const pollingEnabled = pollingForced || pollingCookieOn;
    const { token, softLoggedOut } = useAuth();
    const lastTokenRef = useRef<string | null>(null);

    const lastResyncRef = useRef<number>(0);

    const togglePolling = useCallback(() => {
        setPollingCookieOn(prev => {
            const next = !prev;
            setPollingCookie(next);
            return next;
        });
    }, []);

    /**
     * 復帰トリガーによる強制再同期（スロットル付き）。
     * 手動の「再同期」ボタンからも呼べるよう公開する。
     */
    const triggerResync = useCallback((reason: string) => {
        const now = Date.now();
        if (now - lastResyncRef.current < RESYNC_THROTTLE_MS) {
            return; // 直近に再同期済み（連続発火の抑制）
        }
        lastResyncRef.current = now;
        console.log(`[GlobalSync] Resync triggered: ${reason}`);
        reservationSyncService.forceSync();
        vehicleSyncService.forceSync();
    }, []);

    // 初回データ取得 & 再ログイン検知
    // - 初回: startOnce で認証付きの初回同期
    // - 再ログイン（token が変わった）: forceSync で強制再同期
    // - トークン復活（null → 値あり）: forceSync で再同期
    //   ソフトログアウト中は token=null、Provider は unmount しないため、
    //   token の変化を watch して明示的に再同期する必要がある。
    useEffect(() => {
        if (!enabled || softLoggedOut || !token) {
            return;
        }

        const prev = lastTokenRef.current;
        lastTokenRef.current = token;

        if (prev === null && !reservationSyncService.hasData()) {
            // 完全初回（未ログインからログイン）
            console.log('[GlobalSync] Initial sync on login');
            reservationSyncService.startOnce();
            vehicleSyncService.startOnce();
        } else if (prev !== null && prev !== token) {
            // トークンが変わった = ソフトログアウトからの再ログイン
            console.log('[GlobalSync] Token changed (re-login) — forcing resync');
            reservationSyncService.forceSync();
            vehicleSyncService.forceSync();
        } else if (!reservationSyncService.hasData() || !vehicleSyncService.hasData()) {
            // トークンは同じだがデータが空（過去の同期失敗等）→ 復旧のため再同期
            console.log('[GlobalSync] Cache empty — forcing resync');
            reservationSyncService.forceSync();
            vehicleSyncService.forceSync();
        }
    }, [enabled, token, softLoggedOut]);

    // 復帰トリガー再同期（A）:
    //   - tab が前面に戻った (visibilitychange → visible)
    //   - ネットワーク復帰 (window online)
    //   - Pusher 再接続 (切断中に取りこぼした更新をキャッチアップ)
    //   いずれも「ログオフ・スリープ・タブ非アクティブ・回線瞬断」でズレた状態を
    //   サーバ正本で揃え直すための保険。スロットルで連続発火を抑制。
    useEffect(() => {
        if (!enabled || softLoggedOut || !token) return;

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                triggerResync('visibilitychange→visible');
            }
        };
        const onOnline = () => triggerResync('network online');
        const onReconnect = () => triggerResync('pusher reconnect');

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('online', onOnline);
        const unbindReconnect = pusherService.onReconnect(onReconnect);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('online', onOnline);
            unbindReconnect();
        };
    }, [enabled, token, softLoggedOut, triggerResync]);

    // unmount 時の停止（ハードログアウト等）
    useEffect(() => {
        if (!enabled) return;
        return () => {
            reservationSyncService.stop();
            vehicleSyncService.stop();
            lastTokenRef.current = null;
        };
    }, [enabled]);

    // スタッフ優先度（本日勤務）の初期同期（2026-07-26 追加）:
    //   localStorage を持たない新規ブラウザでも、サーバ保存済みの出勤状態を取り込む。
    //   以降の更新は Pusher（staff-priority.updated）で届く。
    useEffect(() => {
        if (!enabled || softLoggedOut || !token) return;
        syncStaffPrioritiesFromServer().then((updated) => {
            if (updated) console.log('[GlobalSync] Staff priorities synced from server');
        });
    }, [enabled, token, softLoggedOut]);

    // 補助ポーリング（2026-07-26 追加）:
    //   Pusher 主体でポーリングOFFの間（= 那覇のデフォルト状態）も、5分おきにサーバ正本を
    //   取得して Push 通知の取りこぼしを検出・補正する保険。30秒ポーリングON中は不要。
    useEffect(() => {
        if (!enabled || pollingEnabled || softLoggedOut || !token) return;
        const id = setInterval(() => {
            console.log('[GlobalSync] Safety poll (5min) — push取りこぼし検出の補助同期');
            reservationSyncService.forceSync();
            vehicleSyncService.forceSync();
        }, SAFETY_POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [enabled, pollingEnabled, softLoggedOut, token]);

    // ポーリング制御（トグルで即時切り替え）
    // ⚠️ softLoggedOut / token も条件に含めること（2026-08-01）。ソフトログアウト中は
    //    isAuthenticated が true のままなので、ここを見ないと未認証の画面で取得が続く。
    useEffect(() => {
        if (!enabled) return;
        if (softLoggedOut || !token) {
            reservationSyncService.stopPolling();
            vehicleSyncService.stopPolling();
            console.log('[GlobalSync] Polling OFF (ログアウト中)');
            return;
        }
        if (pollingEnabled) {
            reservationSyncService.startPolling();
            vehicleSyncService.startPolling();
            console.log('[GlobalSync] Polling ON');
        } else {
            reservationSyncService.stopPolling();
            vehicleSyncService.stopPolling();
            console.log('[GlobalSync] Polling OFF');
        }
        return () => {
            reservationSyncService.stopPolling();
            vehicleSyncService.stopPolling();
        };
    }, [enabled, pollingEnabled, softLoggedOut, token]);

    return { pollingEnabled, pollingForced, togglePolling, triggerResync };
}

